import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { fmtMoney } from '../../../common/utils/money';
import { useBatch, usePostBatch, usePostLedger } from '../contexts/liquidations';
import { pivotBatchByClient } from '../utils/liquidation-pivot';
import ReverseBatchModal from '../forms/reverse-batch-modal';

import '../../../common/styles/catalog.css';
import '../styles/billing.css';

// Detalle de una liquidación mensual (batch) en PÁGINA COMPLETA. Dos vistas — "Por cliente"
// (tarjetas sobrias que se expanden a la lista de vacas del cliente) y "Por vaca" (tabla
// limpia sin reparto; al tocar una vaca se abre un panel con su detalle + reparto). Montos
// congelados del backend; el front solo reagrupa/filtra (0001).
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const STATUS = {
    draft:     { label: 'Borrador',      tone: 'open' },
    generated: { label: 'Generada',      tone: 'open' },
    posted:    { label: 'Contabilizada', tone: 'good' },
    reversed:  { label: 'Reversada',     tone: 'bad' },
};

// Búsqueda tolerante: minúsculas y sin tildes (strip de diacríticos combinantes U+0300–U+036F).
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Reparto agregado por dueño para el panel de una vaca (varias líneas → un dueño): suma los
// share_amount (montos del backend) y toma el % congelado (por vaca). Planta al final.
function aggregateReparto(lines) {
    const map = new Map();
    for (const l of lines) {
        for (const s of (l.shares || [])) {
            const planta = s.is_planta || !s.owner;
            const key = planta ? '__planta__' : String(s.owner.id);
            let r = map.get(key);
            if (!r) { r = { name: planta ? 'Planta' : s.owner.name, is_planta: planta, percent: s.percent_snapshot, amount: 0 }; map.set(key, r); }
            r.amount += Number(s.share_amount) || 0;
        }
    }
    return [...map.values()].sort((a, b) => (a.is_planta !== b.is_planta ? (a.is_planta ? 1 : -1) : b.amount - a.amount));
}

function ClientCards({ groups, query, onPick }) {
    const q = norm(query);
    const filtered = q
        ? groups.filter((g) => norm(g.is_planta ? 'Planta (residuo)' : g.client?.name).includes(q))
        : groups;

    if (!filtered.length) {
        return <p className="billing-batch__empty">Ningún cliente coincide con «{query}».</p>;
    }
    return (
        <div className="billing-liq__clients">
            {filtered.map((g) => {
                const key = g.is_planta ? 'planta' : g.client.id;
                return (
                    // <details> nativo: colapsado para escanear totales; se expande al buscar (remonta por key).
                    <details key={`${key}-${q ? 'open' : 'closed'}`} open={!!q} className={`billing-liq__client ${g.is_planta ? 'billing-liq__client--planta' : ''}`}>
                        <summary className="billing-liq__client-head">
                            <span className="billing-liq__client-name">{g.is_planta ? 'Planta (residuo)' : g.client.name}</span>
                            <span className="billing-liq__client-meta">{g.cows.length} vaca{g.cows.length === 1 ? '' : 's'}</span>
                            <span className="billing-liq__client-total">{fmtMoney(g.total)}</span>
                        </summary>
                        <div className="billing-liq__client-body">
                            <table className="billing-ctable">
                                <tbody>
                                    {g.cows.map((c, ci) => {
                                        const id = c.animal?.id;
                                        const pick = id != null ? () => onPick(id) : undefined;
                                        return (
                                            <tr
                                                key={id ?? ci}
                                                className={`billing-ctable__row ${pick ? 'billing-ctable__row--click' : ''}`}
                                                onClick={pick}
                                                role={pick ? 'button' : undefined}
                                                tabIndex={pick ? 0 : undefined}
                                                onKeyDown={pick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } } : undefined}
                                            >
                                                <td className="billing-ctable__cow">
                                                    <span className="billing-ctable__cow-name">{c.animal?.name || c.animal?.number || '—'}</span>
                                                    {c.animal?.number && c.animal?.name && <span className="billing-ctable__cow-num">{c.animal.number}</span>}
                                                </td>
                                                <td className="billing-ctable__mgmt">
                                                    {c.lines.map((l, i) => (
                                                        <span key={i} className="billing-ctable__mgmt-item">{l.management || '—'} · {l.days ?? '—'} d</span>
                                                    ))}
                                                </td>
                                                <td className="billing-ctable__amount">{fmtMoney(c.subtotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </details>
                );
            })}
        </div>
    );
}

function CowTable({ liquidations, query, onPick }) {
    const q = norm(query);
    const rows = q
        ? liquidations.filter((l) => norm(l.animal?.name).includes(q) || norm(l.animal?.number).includes(q))
        : liquidations;

    return (
        <div className="cat-overview__card billing-batch__table-wrap">
            <table className="cat-table billing-lines">
                <thead>
                    <tr>
                        <th className="billing-lines__left">Vaca</th>
                        <th className="billing-lines__left">Manejo</th>
                        <th className="billing-lines__num">Días</th>
                        <th className="billing-lines__num">Tarifa/día</th>
                        <th className="billing-lines__num">Pensión</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((l) => {
                        const id = l.animal?.id;
                        const pick = id != null ? () => onPick(id) : undefined;
                        return (
                            <tr
                                key={l.id}
                                className={`cat-table__row ${pick ? 'billing-lines__row--click' : ''}`}
                                onClick={pick}
                                role={pick ? 'button' : undefined}
                                tabIndex={pick ? 0 : undefined}
                                onKeyDown={pick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } } : undefined}
                            >
                                <td className="billing-lines__left">
                                    <span className="cat-table__name">{l.animal?.name || l.animal?.number || `#${l.animal?.id}`}</span>
                                    {l.animal?.number && l.animal?.name && <span className="billing-lines__muted"> · {l.animal.number}</span>}
                                </td>
                                <td className="billing-lines__left">{l.management?.name || '—'}</td>
                                <td className="billing-lines__num">{l.days ?? '—'}</td>
                                <td className="billing-lines__num">{fmtMoney(l.day_rate_snapshot)}</td>
                                <td className="billing-lines__num billing-lines__strong">{fmtMoney(l.pension_amount)}</td>
                            </tr>
                        );
                    })}
                    {!rows.length && (
                        <tr><td colSpan={5} className="cat-table__empty" style={{ textAlign: 'center' }}>{q ? `Ninguna vaca coincide con «${query}».` : 'Sin líneas.'}</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// Panel lateral con el detalle de la liquidación de UNA vaca: sus líneas de manejo
// (días/tarifa/pensión) y el reparto por dueño (%, monto). Montos del backend.
function CowDetailPanel({ animalId, liquidations, onClose }) {
    const cardRef = useModalDialog(onClose);
    const lines = useMemo(() => liquidations.filter((l) => l.animal?.id === animalId), [liquidations, animalId]);
    const reparto = useMemo(() => aggregateReparto(lines), [lines]);
    const animal = lines[0]?.animal;
    const total = lines.reduce((a, l) => a + (Number(l.pension_amount) || 0), 0);
    const label = animal ? (animal.name || animal.number || `#${animalId}`) : `#${animalId}`;

    return (
        <div className="billing-panel" role="dialog" aria-modal="true" aria-label={`Liquidación de ${label}`}>
            <div className="billing-panel__overlay" onClick={onClose} aria-hidden="true" />
            <aside className="billing-panel__card" ref={cardRef}>
                <header className="billing-panel__head">
                    <div className="billing-panel__heading">
                        <span className="billing-panel__eyebrow">Liquidación de la vaca</span>
                        <h2 className="billing-panel__title">{label}</h2>
                        {animal?.number && animal?.name && <span className="billing-panel__sub">{animal.number}</span>}
                    </div>
                    <button type="button" className="billing-panel__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="billing-panel__total">
                    <span className="billing-panel__total-label">Pensión del período</span>
                    <span className="billing-panel__total-value">{fmtMoney(total)}</span>
                </div>

                <section className="billing-panel__section">
                    <h3 className="billing-panel__section-title">Manejo</h3>
                    <ul className="billing-panel__lines">
                        {lines.map((l, i) => (
                            <li key={l.id ?? i} className="billing-panel__line">
                                <div className="billing-panel__line-top">
                                    <span className="billing-panel__line-mgmt">{l.management?.name || '—'}</span>
                                    <span className="billing-panel__line-amount">{fmtMoney(l.pension_amount)}</span>
                                </div>
                                <span className="billing-panel__line-meta">{l.days ?? '—'} días · {fmtMoney(l.day_rate_snapshot)}/día</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="billing-panel__section">
                    <h3 className="billing-panel__section-title">Reparto por dueño</h3>
                    <ul className="billing-panel__reparto">
                        {reparto.map((r, i) => (
                            <li key={i} className={`billing-panel__owner ${r.is_planta ? 'billing-panel__owner--planta' : ''}`}>
                                <span className="billing-panel__owner-name">{r.name}</span>
                                {r.percent != null && <span className="billing-panel__owner-pct">{r.percent}%</span>}
                                <span className="billing-panel__owner-amount">{fmtMoney(r.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            </aside>
        </div>
    );
}

export default function BatchDetail({ batchId }) {
    const { data, isLoading, error } = useBatch(batchId);
    const { mutateAsync: post, isPending: posting } = usePostBatch();
    const { mutateAsync: ledger, isPending: ledgering } = usePostLedger();
    const busy = posting || ledgering;

    const [view, setView] = useState('client'); // 'client' (default) | 'cow'
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState(null); // animal_id del panel abierto
    const [reversing, setReversing] = useState(false); // modal de confirmación de reversa

    const batch = data?.batch;
    const liquidations = useMemo(() => data?.liquidations || [], [data]);
    const clientGroups = useMemo(() => pivotBatchByClient(liquidations), [liquidations]);
    const st = batch && STATUS[batch.status];
    const clientCount = clientGroups.filter((g) => !g.is_planta).length;

    const doPost = async () => {
        try { await post(batchId); toast.success('Liquidación contabilizada'); }
        catch (er) { if (er?.status === 409) toast.error('No está en estado "generada"'); else if (er?.status !== 403) toast.error(er?.message || 'No se pudo contabilizar'); }
    };
    const doLedger = async () => {
        try { await ledger(batchId); toast.success('Pensión asentada al ledger'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo asentar'); }
    };

    const pickView = (v) => { setView(v); setQuery(''); setPicked(null); };

    return (
        <div className="billing-liq billing-batch">
            <header className="billing-liq__header">
                <div>
                    <Link to="/admin/billing?tab=liquidations" className="billing-liq__back">Facturación · Liquidaciones</Link>
                    <h1 className="billing-liq__title">
                        Liquidación · {batch ? `${MONTHS[(batch.period_month || 1) - 1]} ${batch.period_year}` : '…'}
                        {st && <span className={`billing-tag billing-tag--${st.tone} billing-batch__status`}>{st.label}</span>}
                    </h1>
                    {batch && <p className="billing-batch__meta">{batch.animal_count} vaca(s) · {batch.liquidation_count} línea(s) · {clientCount} cliente(s)</p>}
                </div>
                <Gate domain="billing" action="write">
                    <div className="billing-batch__actions">
                        {batch?.status === 'generated' && <button type="button" className="billing-btn billing-btn--primary" onClick={doPost} disabled={busy}>{posting ? 'Contabilizando…' : 'Contabilizar'}</button>}
                        {batch?.status === 'posted' && <button type="button" className="billing-btn billing-btn--primary" onClick={doLedger} disabled={busy}>{ledgering ? 'Asentando…' : 'Asentar al ledger'}</button>}
                        {batch?.status === 'posted' && <button type="button" className="billing-btn billing-btn--danger-outline" onClick={() => setReversing(true)} disabled={busy}>Revertir</button>}
                    </div>
                </Gate>
            </header>

            {error ? (
                <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver esta liquidación.' : (error.message || 'No se pudo cargar el detalle.')}</div>
            ) : isLoading ? (
                <div className="cat-overview__state">Cargando liquidación…</div>
            ) : (
                <>
                    <div className="billing-liq__total-card">
                        <span className="billing-liq__total-label">Total del período</span>
                        <span className="billing-liq__total-value">{fmtMoney(batch?.totals?.pension_total)}</span>
                    </div>

                    <div className="cat-overview__toolbar billing-batch__toolbar">
                        <div className="billing-seg" role="tablist" aria-label="Cómo ver la liquidación">
                            <button type="button" role="tab" aria-selected={view === 'client'} className={`billing-seg__btn ${view === 'client' ? 'billing-seg__btn--active' : ''}`} onClick={() => pickView('client')}>Por cliente</button>
                            <button type="button" role="tab" aria-selected={view === 'cow'} className={`billing-seg__btn ${view === 'cow' ? 'billing-seg__btn--active' : ''}`} onClick={() => pickView('cow')}>Por vaca</button>
                        </div>
                        <div className="cat-overview__search">
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={view === 'client' ? 'Buscar cliente…' : 'Buscar vaca (nombre o número)…'}
                                aria-label={view === 'client' ? 'Buscar cliente' : 'Buscar vaca'}
                            />
                        </div>
                        {query && <button type="button" className="cat-overview__clear" onClick={() => setQuery('')}>Limpiar</button>}
                    </div>

                    {view === 'client' ? (
                        !clientGroups.length
                            ? <div className="cat-overview__state"><p className="cat-overview__state-text">Sin reparto para este período.</p></div>
                            : <ClientCards groups={clientGroups} query={query} onPick={setPicked} />
                    ) : (
                        <CowTable liquidations={liquidations} query={query} onPick={setPicked} />
                    )}
                </>
            )}

            {picked != null && <CowDetailPanel animalId={picked} liquidations={liquidations} onClose={() => setPicked(null)} />}
            {reversing && batch && <ReverseBatchModal batch={batch} onClose={() => setReversing(false)} onReversed={() => setReversing(false)} />}
        </div>
    );
}
